import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import {
  FiGrid, FiPlus, FiSearch, FiFilter, FiCalendar, FiUsers, FiClock,
  FiAward, FiArchive, FiRefreshCw, FiCopy, FiArrowRight, FiCheckCircle,
  FiLayers, FiCode, FiCpu, FiShield, FiBarChart2, FiRadio, FiSmartphone,
  FiLayout, FiEdit3, FiTrash2, FiTag, FiFolder, FiCheck, FiX, FiChevronDown
} from 'react-icons/fi';

const STATUS_COLORS = {
  Active: 'badge-active',
  Upcoming: 'badge-upcoming',
  Completed: 'badge-completed',
  Archived: 'badge-archived',
  Draft: 'bg-purple-50 text-purple-700 border border-purple-100',
};

const MONTHS = ['All Months', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = ['All Years', '2026', '2027', '2028'];

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] } }
};

export default function InternshipPrograms() {
  const navigate = useNavigate();
  const {
    programs, addProgram, updateProgram, archiveProgram, reopenProgram, duplicateProgram,
    categories, addCategory, deleteCategory, batches, candidates, setActiveProgramId
  } = usePlatform();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedYear, setSelectedYear] = useState('All Years');
  const [selectedMonth, setSelectedMonth] = useState('All Months');
  const [viewTab, setViewTab] = useState('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [duplicateModalProg, setDuplicateModalProg] = useState(null);
  const [dupMonth, setDupMonth] = useState('October');
  const [dupYear, setDupYear] = useState(2026);

  const [newProg, setNewProg] = useState({
    name: '', categoryId: categories[0]?.id || '', domain: '', duration: '1 Month',
    startDate: '2026-09-01', endDate: '2026-09-30', applicationStartDate: '2026-08-01',
    applicationEndDate: '2026-08-25', mode: 'Online', location: 'Remote / Virtual',
    mentor: 'Technical Team Lead', status: 'Active', maximumSeats: 250,
    type: 'Summer / Technical Internship', idPrefix: 'IH-FS', year: 2026, month: 'September', description: '',
  });
  const [newCatName, setNewCatName] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');
  const [newCatColor, setNewCatColor] = useState('#0ea5e9');

  const filteredPrograms = useMemo(() => {
    return programs.filter(p => {
      if (viewTab === 'active' && p.isArchived) return false;
      if (viewTab === 'archived' && !p.isArchived) return false;
      if (selectedStatus !== 'All' && p.status !== selectedStatus) return false;
      if (selectedCategory !== 'All' && p.categoryId !== selectedCategory) return false;
      if (selectedYear !== 'All Years' && String(p.year) !== selectedYear) return false;
      if (selectedMonth !== 'All Months' && p.month !== selectedMonth) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q) ||
                p.domain?.toLowerCase().includes(q) || p.mentor?.toLowerCase().includes(q) ||
                p.categoryName?.toLowerCase().includes(q));
      }
      return true;
    });
  }, [programs, viewTab, selectedStatus, selectedCategory, selectedYear, selectedMonth, searchQuery]);

  const stats = useMemo(() => ({
    total: programs.length,
    active: programs.filter(p => p.status === 'Active' && !p.isArchived).length,
    upcoming: programs.filter(p => p.status === 'Upcoming' && !p.isArchived).length,
    completed: programs.filter(p => p.status === 'Completed' && !p.isArchived).length,
    archived: programs.filter(p => p.isArchived).length,
  }), [programs]);

  const handleCreateProgram = (e) => {
    e.preventDefault();
    if (!newProg.name.trim()) return toast.error('Please enter a program name.');
    const cat = categories.find(c => c.id === newProg.categoryId);
    const created = addProgram({ ...newProg, domain: newProg.domain || cat?.name || 'Technology' });
    toast.success(`Program "${created.name}" created successfully!`);
    setShowCreateModal(false);
  };

  const handleAddCategory = (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return toast.error('Please enter a category name.');
    addCategory({ name: newCatName, description: newCatDesc, color: newCatColor });
    toast.success(`Category "${newCatName}" added!`);
    setNewCatName(''); setNewCatDesc(''); setShowCategoryModal(false);
  };

  const handleDuplicate = () => {
    if (!duplicateModalProg) return;
    const dup = duplicateProgram(duplicateModalProg.id, dupMonth, dupYear);
    if (dup) { toast.success(`Created duplicate: ${dup.name}`); setDuplicateModalProg(null); }
  };

  const openProgramDetail = (programId) => {
    setActiveProgramId(programId);
    navigate(`/programs/${programId}`);
  };

  const hasActiveFilters = selectedCategory !== 'All' || selectedStatus !== 'All' || selectedYear !== 'All Years' || selectedMonth !== 'All Months' || searchQuery;

  const FormLabel = ({ children }) => (
    <label className="block text-xs font-medium text-slate-500 mb-1.5">{children}</label>
  );

  return (
    <div className="space-y-8 pb-16">
      {/* ── Page Header ─────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="page-title">Internship Programs</h1>
          <p className="page-subtitle">
            Create, manage, and archive internship programs across categories, batches, and years.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowCategoryModal(true)} className="btn-secondary text-xs">
            <FiTag className="w-3.5 h-3.5" /> Categories ({categories.length})
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary text-xs">
            <FiPlus className="w-4 h-4" /> Create Internship
          </button>
        </div>
      </motion.div>

      {/* ── Stat Cards ──────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
        {[
          { label: 'Total', val: stats.total, color: 'text-slate-950', icon: FiLayers, iconBg: 'bg-slate-100 text-slate-700', iconBorder: 'border-slate-300' },
          { label: 'Active', val: stats.active, color: 'text-emerald-700', icon: FiCheckCircle, iconBg: 'bg-emerald-50 text-emerald-600', iconBorder: 'border-emerald-200' },
          { label: 'Upcoming', val: stats.upcoming, color: 'text-blue-700', icon: FiClock, iconBg: 'bg-blue-50 text-blue-600', iconBorder: 'border-blue-200' },
          { label: 'Completed', val: stats.completed, color: 'text-violet-700', icon: FiAward, iconBg: 'bg-violet-50 text-violet-600', iconBorder: 'border-violet-200' },
          { label: 'Archived', val: stats.archived, color: 'text-amber-700', icon: FiArchive, iconBg: 'bg-amber-50 text-amber-600', iconBorder: 'border-amber-200' },
        ].map(s => (
          <div key={s.label} className="bg-white border-2 border-slate-200 rounded-2xl p-6 flex items-center gap-4 hover:border-slate-300 hover:shadow-md transition-all">
            <div className={`w-12 h-12 rounded-2xl ${s.iconBg} border-2 ${s.iconBorder} flex items-center justify-center flex-shrink-0`}>
              <s.icon className="w-6 h-6" />
            </div>
            <div>
              <div className={`text-3xl font-black ${s.color}`}>{s.val}</div>
              <div className="text-xs text-slate-500 font-black uppercase tracking-widest mt-0.5">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs & Filters ──────────────────────────────── */}
      <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-1.5 bg-slate-100 p-1.5 rounded-xl border-2 border-slate-200">
            <button
              onClick={() => setViewTab('active')}
              className={`px-5 py-2 rounded-lg text-xs font-black transition-all ${viewTab === 'active' ? 'bg-white text-blue-700 shadow-sm border-2 border-slate-200' : 'text-slate-600 hover:text-slate-950'}`}>
              Active & Upcoming ({programs.filter(p => !p.isArchived).length})
            </button>
            <button
              onClick={() => setViewTab('archived')}
              className={`px-5 py-2 rounded-lg text-xs font-black transition-all flex items-center gap-1.5 ${viewTab === 'archived' ? 'bg-white text-amber-700 shadow-sm border-2 border-slate-200' : 'text-slate-600 hover:text-slate-950'}`}>
              <FiArchive className="w-3.5 h-3.5" /> Archive ({stats.archived})
            </button>
          </div>

          <div className="relative min-w-[240px] flex-1 max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
            <input
              type="text" placeholder="Search programs..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-11 pr-4 bg-white border-2 border-slate-200 rounded-xl text-slate-950 font-bold text-xs placeholder:text-slate-400 placeholder:font-normal focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none transition-all"
            />
          </div>
        </div>

        <div className="pt-4 border-t-2 border-slate-200 flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5 text-slate-950 font-black">
            <FiFilter className="w-3.5 h-3.5 text-blue-600" /> Filters:
          </div>
          <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
            className="h-9 px-3 bg-white border-2 border-slate-200 rounded-xl text-xs font-bold text-slate-950 cursor-pointer outline-none focus:border-blue-500">
            <option value="All">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}
            className="h-9 px-3 bg-white border-2 border-slate-200 rounded-xl text-xs font-bold text-slate-950 cursor-pointer outline-none focus:border-blue-500">
            <option value="All">All Statuses</option>
            {['Active', 'Upcoming', 'Completed', 'Draft'].map(st => <option key={st} value={st}>{st}</option>)}
          </select>
          <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
            className="h-9 px-3 bg-white border-2 border-slate-200 rounded-xl text-xs font-bold text-slate-950 cursor-pointer outline-none focus:border-blue-500">
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            className="h-9 px-3 bg-white border-2 border-slate-200 rounded-xl text-xs font-bold text-slate-950 cursor-pointer outline-none focus:border-blue-500">
            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          {hasActiveFilters && (
            <button
              onClick={() => { setSelectedCategory('All'); setSelectedStatus('All'); setSelectedYear('All Years'); setSelectedMonth('All Months'); setSearchQuery(''); }}
              className="text-blue-700 hover:text-blue-900 font-black ml-auto flex items-center gap-1 text-xs">
              <FiX className="w-3.5 h-3.5" /> Clear All
            </button>
          )}
        </div>
      </div>

      {/* ── Program Cards ───────────────────────────────── */}
      {filteredPrograms.length === 0 ? (
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-20 text-center">
          <div className="w-16 h-16 bg-slate-100 border-2 border-slate-200 rounded-2xl flex items-center justify-center text-slate-400 mx-auto mb-5">
            <FiFolder className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-black text-slate-950">No Programs Found</h3>
          <p className="text-sm text-slate-500 font-semibold mt-2 max-w-sm mx-auto">
            {viewTab === 'archived'
              ? 'No archived programs match your filters.'
              : 'Create your first internship program to get started.'}
          </p>
          {viewTab === 'active' && (
            <button onClick={() => setShowCreateModal(true)} className="btn-primary text-xs mt-6 font-bold">
              <FiPlus className="w-3.5 h-3.5" /> Create Internship
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrograms.map(p => {
            const programBatches = batches.filter(b => b.programId === p.id);
            const programCandidates = candidates.filter(c => c.programId === p.id);

            return (
              <motion.div
                key={p.id} variants={fadeUp} initial="hidden" animate="visible"
                className={`bg-white rounded-3xl p-7 flex flex-col justify-between border-2 border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all ${p.isArchived ? 'opacity-75' : ''}`}
              >
                <div className="space-y-4">
                  {/* Category & Status */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-black text-brand-700 bg-brand-50 px-2.5 py-1 rounded-lg border border-brand-200">
                      {p.categoryName || p.domain}
                    </span>
                    <span className={`badge ${STATUS_COLORS[p.status] || STATUS_COLORS.Active} font-bold`}>
                      {p.status}
                    </span>
                  </div>

                  {/* Title */}
                  <div>
                    <h3 className="text-base font-black text-slate-950 leading-snug">{p.name}</h3>
                    <div className="text-xs text-slate-800 font-bold mt-1">{p.month} {p.year} · {p.code}</div>
                  </div>

                  {/* Details */}
                  <div className="p-5 bg-slate-50 rounded-2xl border-2 border-slate-200 text-xs space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 font-black uppercase tracking-widest">Timeline</span>
                      <span className="font-black text-slate-950 font-mono text-[11px]">{p.startDate} → {p.endDate}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 font-black uppercase tracking-widest">Duration</span>
                      <span className="font-black text-slate-950">{p.duration} · {p.mode}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 font-black uppercase tracking-widest">Batches</span>
                      <span className="font-black text-blue-700">{programBatches.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-900 font-bold">Candidates</span>
                      <span className="font-black text-emerald-700">{programCandidates.length}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 mt-4 border-t border-surface-100 flex items-center gap-2">
                  <button onClick={() => openProgramDetail(p.id)}
                    className="btn-primary flex-1 text-xs py-2.5">
                    View Program <FiArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDuplicateModalProg(p)} title="Duplicate"
                    className="p-2.5 bg-surface-100 text-slate-500 hover:bg-brand-50 hover:text-brand-600 rounded-xl transition-all">
                    <FiCopy className="w-4 h-4" />
                  </button>
                  {p.isArchived ? (
                    <button onClick={() => { reopenProgram(p.id); toast.success(`"${p.name}" reopened!`); }} title="Reopen"
                      className="p-2.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-xl transition-all">
                      <FiRefreshCw className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => {
                      if (confirm(`Archive "${p.name}"? Records remain accessible.`)) {
                        archiveProgram(p.id); toast.success(`"${p.name}" archived.`);
                      }
                    }} title="Archive"
                      className="p-2.5 bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-xl transition-all">
                      <FiArchive className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── CREATE MODAL ─────────────────────────────────── */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Internship Program" size="lg">
        <form onSubmit={handleCreateProgram} className="p-7 space-y-6">
          {/* Section: Program Info */}
          <div>
            <h3 className="text-sm font-semibold text-navy-900 mb-4 flex items-center gap-2">
              <div className="w-1 h-4 bg-brand-500 rounded-full" /> Program Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FormLabel>Program Name *</FormLabel>
                <input required placeholder="e.g. AI & Machine Learning Internship"
                  value={newProg.name} onChange={e => setNewProg({ ...newProg, name: e.target.value })}
                  className="input-field" />
              </div>
              <div>
                <FormLabel>Category *</FormLabel>
                <select value={newProg.categoryId} onChange={e => setNewProg({ ...newProg, categoryId: e.target.value })}
                  className="input-field">
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <FormLabel>Domain / Specialization</FormLabel>
                <input placeholder="e.g. Artificial Intelligence"
                  value={newProg.domain} onChange={e => setNewProg({ ...newProg, domain: e.target.value })}
                  className="input-field" />
              </div>
            </div>
          </div>

          {/* Section: Dates */}
          <div>
            <h3 className="text-sm font-semibold text-navy-900 mb-4 flex items-center gap-2">
              <div className="w-1 h-4 bg-violet-500 rounded-full" /> Schedule & Duration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <FormLabel>Start Date</FormLabel>
                <input type="date" value={newProg.startDate}
                  onChange={e => setNewProg({ ...newProg, startDate: e.target.value })} className="input-field" />
              </div>
              <div>
                <FormLabel>End Date</FormLabel>
                <input type="date" value={newProg.endDate}
                  onChange={e => setNewProg({ ...newProg, endDate: e.target.value })} className="input-field" />
              </div>
              <div>
                <FormLabel>Duration</FormLabel>
                <input placeholder="e.g. 1 Month" value={newProg.duration}
                  onChange={e => setNewProg({ ...newProg, duration: e.target.value })} className="input-field" />
              </div>
            </div>
          </div>

          {/* Section: Config */}
          <div>
            <h3 className="text-sm font-semibold text-navy-900 mb-4 flex items-center gap-2">
              <div className="w-1 h-4 bg-emerald-500 rounded-full" /> Configuration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormLabel>Mode</FormLabel>
                <select value={newProg.mode} onChange={e => setNewProg({ ...newProg, mode: e.target.value })} className="input-field">
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>
              <div>
                <FormLabel>Mentor / Lead</FormLabel>
                <input placeholder="e.g. Technical Team Lead" value={newProg.mentor}
                  onChange={e => setNewProg({ ...newProg, mentor: e.target.value })} className="input-field" />
              </div>
              <div>
                <FormLabel>Maximum Capacity</FormLabel>
                <input type="number" value={newProg.maximumSeats}
                  onChange={e => setNewProg({ ...newProg, maximumSeats: e.target.value })} className="input-field" />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <FormLabel>Program Overview</FormLabel>
            <textarea rows={3} placeholder="Brief description of the internship..."
              value={newProg.description} onChange={e => setNewProg({ ...newProg, description: e.target.value })}
              className="input-field resize-none" />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-surface-100">
            <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary text-xs">Cancel</button>
            <button type="submit" className="btn-primary text-xs">Create Program & Initial Batch</button>
          </div>
        </form>
      </Modal>

      {/* ── CATEGORIES MODAL ─────────────────────────────── */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)} title="Manage Categories" size="md">
        <div className="p-7 space-y-6">
          <form onSubmit={handleAddCategory} className="p-4 bg-surface-50 rounded-xl border border-surface-200 space-y-3">
            <div className="text-xs font-semibold text-navy-900">Add New Category</div>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Category Name (e.g. AI / ML)"
                value={newCatName} onChange={e => setNewCatName(e.target.value)} className="input-field text-xs" />
              <input placeholder="Description" value={newCatDesc}
                onChange={e => setNewCatDesc(e.target.value)} className="input-field text-xs" />
            </div>
            <div className="flex justify-end">
              <button type="submit" className="btn-primary text-xs py-2">Save Category</button>
            </div>
          </form>

          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            <div className="section-title mb-2">Active Categories ({categories.length})</div>
            {categories.map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-white border border-surface-200 rounded-xl hover:border-brand-200 transition-all text-sm">
                <div className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || '#0ea5e9' }} />
                  <span className="font-medium text-navy-900">{c.name}</span>
                  {c.description && <span className="text-xs text-slate-800 font-semibold truncate max-w-[200px]">({c.description})</span>}
                </div>
                {categories.length > 1 && (
                  <button onClick={() => { if (confirm(`Delete "${c.name}"?`)) deleteCategory(c.id); }}
                    className="p-1.5 text-slate-300 hover:text-red-600 rounded-lg transition-colors">
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* ── DUPLICATE MODAL ──────────────────────────────── */}
      <Modal isOpen={!!duplicateModalProg} onClose={() => setDuplicateModalProg(null)}
        title={`Duplicate — ${duplicateModalProg?.name}`} size="sm">
        {duplicateModalProg && (
          <div className="p-7 space-y-5">
            <p className="text-sm text-slate-500 leading-relaxed">
              Creates a new batch/cycle with identical configurations and a fresh candidate list.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FormLabel>Target Month</FormLabel>
                <select value={dupMonth} onChange={e => setDupMonth(e.target.value)} className="input-field text-xs">
                  {MONTHS.filter(m => m !== 'All Months').map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <FormLabel>Target Year</FormLabel>
                <select value={dupYear} onChange={e => setDupYear(Number(e.target.value))} className="input-field text-xs">
                  <option value={2026}>2026</option>
                  <option value={2027}>2027</option>
                  <option value={2028}>2028</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-surface-100">
              <button onClick={() => setDuplicateModalProg(null)} className="btn-secondary text-xs">Cancel</button>
              <button onClick={handleDuplicate} className="btn-primary text-xs">Confirm & Create</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
